import React, { useEffect, useMemo, useState } from 'react';
import './Issues.css';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { getApiUrl } from '../utils/api';
import { getProjectRoleBadgeInfo } from '../utils/roleBadges';
import DeleteIssueModal from './modals/DeleteIssueModal';

const STATUS_LABELS = {
  open: 'Aberto',
  in_progress: 'Em correção',
  reopened: 'Reaberto',
  fixed: 'Corrigido',
  rejected: 'Reprovado',
  approved: 'Aprovado',
  archived: 'Arquivado',
};

const CASE_STATUS_LABELS = {
  planned: 'Planejado',
  pending: 'Pendente',
  running: 'Executando',
  completed: 'Concluído',
  bug: 'Bug',
  improvement: 'Melhoria',
  retest: 'Reteste',
  blocked: 'Bloqueado',
  passed: 'Aprovado',
  failed: 'Falha',
  skipped: 'Ignorado',
  warning: 'Atenção',
};

const TYPE_LABELS = {
  bug: 'Bug',
  improvement: 'Melhoria',
};

const PRIORITY_LABELS = {
  lowest: 'Muito baixa',
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  highest: 'Muita alta',
  critical: 'Muita alta',
  mdium: 'Média',
};

const PRIORITY_OPTIONS = [
  { value: 'lowest', label: 'Muito baixa' },
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'highest', label: 'Muita alta' },
];

function formatDate(dt) {
  if (!dt) return '-';
  const date = new Date(dt);
  return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function Issues() {
  const { user, hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtros
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [responsibleId, setResponsibleId] = useState('');
  const [qaOwnerId, setQaOwnerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [projects, setProjects] = useState([]);
  const [filterDevs, setFilterDevs] = useState([]);
  const [filterQas, setFilterQas] = useState([]);

  // Modal de detalhes
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [newComment, setNewComment] = useState('');
  const MAX_COMMENT_CHARS = 500;
  const overLimit = newComment.length > MAX_COMMENT_CHARS;
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [projectDevs, setProjectDevs] = useState([]);
  const [selectedResponsibleDev, setSelectedResponsibleDev] = useState(''); // user_id resolvido (só some ok pra "Definir" quando o texto digitado bate com um DEV de verdade)
  const [responsibleDevInput, setResponsibleDevInput] = useState(''); // texto exibido/digitado no campo
  const [assigningResponsible, setAssigningResponsible] = useState(false);
  const [updatingPriorityId, setUpdatingPriorityId] = useState(null);
  const [previewEvidence, setPreviewEvidence] = useState(null);
  const [loadingPreviewEvidenceId, setLoadingPreviewEvidenceId] = useState(null);
  // Modal de confirmação de exclusão
  const [showDeleteIssueModal, setShowDeleteIssueModal] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState(null);

  const canView = hasPermission('issues.read', ['ADM','ADMIN','GERENCIAL','DEV','QA']);
  const canDevUpdate = hasPermission('issues.update.dev', ['ADM','ADMIN','DEV']);
  const canQaUpdate = hasPermission('issues.update.qa', ['ADM','ADMIN','QA']);
  const canDelete = hasPermission('issues.delete', ['ADM','ADMIN','QA']);
  const canDeleteUI = hasPermission('issues.delete', ['ADM','ADMIN','DEV']);
  const roleUpper = (user?.role || '').toUpperCase();
  const isDev = roleUpper === 'DEV';
  const isQaLike = roleUpper === 'QA' || roleUpper === 'TRIAL' || roleUpper === 'UNITARIO';
  const hideResponsibleFilter = isQaLike && roleUpper !== 'QA';
  const isGerencial = roleUpper === 'GERENCIAL';
  const canEditPriority = ['ADM','ADMIN','DEV','QA','TRIAL','UNITARIO'].includes(roleUpper);

  const statusOptions = useMemo(() => ([
    { value: '', label: 'Todos' },
    { value: 'bug', label: CASE_STATUS_LABELS.bug },
    { value: 'improvement', label: CASE_STATUS_LABELS.improvement },
    { value: 'retest', label: CASE_STATUS_LABELS.retest },
    { value: 'running', label: CASE_STATUS_LABELS.running },
    { value: 'planned', label: CASE_STATUS_LABELS.planned },
    { value: 'completed', label: CASE_STATUS_LABELS.completed },
    { value: 'pending', label: CASE_STATUS_LABELS.pending },
    { value: 'blocked', label: CASE_STATUS_LABELS.blocked },
  ]), []);

  const typeOptions = useMemo(() => ([
    { value: '', label: 'Todos' },
    { value: 'bug', label: TYPE_LABELS.bug },
    { value: 'improvement', label: TYPE_LABELS.improvement },
  ]), []);

  const formatCompactId = (id) => {
    const value = String(id || '').replace(/^#/, '');
    return value.slice(0, 3);
  };

  const copyToClipboard = async (value) => {
    const text = String(value || '');
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showSuccess('ID copiado');
    } catch (_) {
      showError('Não foi possível copiar o ID');
    }
  };

  const renderCompactIdCell = (id) => (
    <div className="compact-id" title={String(id || '')}>
      <span className="compact-id-text">{formatCompactId(id)}</span>
      <button
        type="button"
        className="compact-id-copy"
        onClick={(event) => {
          event.stopPropagation();
          copyToClipboard(id);
        }}
        title="Copiar ID completo"
        aria-label="Copiar ID completo"
      >
        📋
      </button>
    </div>
  );

  async function fetchList() {
    if (!canView) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('q', search); // alinhar com backend
      if (type) params.append('type', type);
      if (status) params.append('status', status);
      if (responsibleId) params.append('responsible_id', responsibleId);
      if (qaOwnerId) params.append('qa_owner_id', qaOwnerId);
      if (projectId) params.append('project_id', projectId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await fetch(getApiUrl(`issues?${params.toString()}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setItems(result.data || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Erro ao carregar issues');
      }
    } catch (err) {
      console.error('Erro ao buscar issues:', err);
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }

  async function fetchProjects() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('projects'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setProjects([]);
        return;
      }
      const list = Array.isArray(payload?.projects)
        ? payload.projects
        : (Array.isArray(payload) ? payload : []);
      setProjects(list);
    } catch (_) {
      setProjects([]);
    }
  }

  async function fetchFilterDevsForProjects(projectIds = []) {
    try {
      const token = localStorage.getItem('token');
      const unique = Array.from(new Set((projectIds || []).filter(Boolean).map(String)));
      if (unique.length === 0) {
        setFilterDevs([]);
        return;
      }
      const reqs = unique.map(pid => fetch(getApiUrl(`issues/project/${pid}/devs`), { headers: { 'Authorization': `Bearer ${token}` } }));
      const resps = await Promise.all(reqs);
      const payloads = await Promise.all(resps.map(r => r.json().catch(() => ({ data: [] }))));
      const merged = [];
      const seen = new Set();
      payloads.forEach(p => {
        const arr = Array.isArray(p?.data) ? p.data : [];
        arr.forEach(u => {
          const idStr = String(u.user_id);
          if (!seen.has(idStr)) {
            seen.add(idStr);
            merged.push(u);
          }
        });
      });
      setFilterDevs(merged);
    } catch (_) {
      setFilterDevs([]);
    }
  }

  async function fetchCompanyDevs() {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl('issues/company/devs'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setFilterDevs([]);
        return;
      }
      const arr = Array.isArray(payload?.data) ? payload.data : [];
      setFilterDevs(arr);
    } catch (_) {
      setFilterDevs([]);
    }
  }

  async function fetchCompanyQas() {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl('issues/company/qas'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setFilterQas([]);
        return;
      }
      const arr = Array.isArray(payload?.data) ? payload.data : [];
      setFilterQas(arr);
    } catch (_) {
      setFilterQas([]);
    }
  }

  useEffect(() => {
    if (canView) {
      fetchProjects();
      fetchList();
    }
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    fetchList();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchDetail(id) {
    setDetailLoading(true);
    setDetailError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setDetail(result.data);
      } else {
        setDetailError('Erro ao carregar detalhes');
      }
    } catch (err) {
      setDetailError('Erro de conexão');
    } finally {
      setDetailLoading(false);
    }
  }

  function openDetail(id) {
    setSelectedId(id);
    setShowModal(true);
    fetchDetail(id);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedId(null);
    setDetail(null);
    setNewComment('');
    closePreviewEvidence();
  }

  function closePreviewEvidence() {
    setPreviewEvidence(prev => {
      if (prev?.isObjectUrl && prev?.url) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
  }

  async function handleAddComment() {
    if (!newComment.trim()) return;
    if (overLimit) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${selectedId}/comments`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment: newComment })
      });
      if (response.ok) {
        setNewComment('');
        fetchDetail(selectedId); // Recarregar detalhes
      }
    } catch (err) {
      console.error('Erro ao adicionar comentário:', err);
    }
  }

  async function handleUpdateStatus(nextStatus) {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${selectedId}/status`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: nextStatus })
      });
      if (response.ok) {
        fetchDetail(selectedId); // Recarregar detalhes
        fetchList(); // Recarregar lista
      }
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  }

  async function handleClaim() {
    if (!selectedId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${selectedId}/claim`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        await fetchDetail(selectedId);
        await fetchList();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Não foi possível reivindicar este item');
      }
    } catch (err) {
      console.error('Erro ao reivindicar issue:', err);
      alert('Erro de conexão ao reivindicar');
    }
  }

  async function fetchProjectDevs(projectId) {
    if (!projectId) {
      setProjectDevs([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`issues/project/${projectId}/devs`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payload = await res.json().catch(() => ({}));
      const items = Array.isArray(payload?.data) ? payload.data : [];
      const devs = items.map(u => ({ user_id: u.user_id, name: u.name || u.email || `Usuário ${u.user_id}` }));
      setProjectDevs(devs);
    } catch (e) {
      setProjectDevs([]);
    }
  }

  async function handleAssignResponsible() {
    if (!selectedId || !selectedResponsibleDev) return;
    setAssigningResponsible(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${selectedId}/responsible`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: selectedResponsibleDev })
      });
      if (response.ok) {
        await fetchDetail(selectedId);
        await fetchList();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Não foi possível definir o responsável');
      }
    } catch (err) {
      alert('Erro de conexão ao definir responsável');
    } finally {
      setAssigningResponsible(false);
    }
  }

  async function handleSyncWithJira() {
    try {
      const key = detail?.issue?.jira_issue_key;
      if (!key) return;
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl(`integrations/jira/sync/${encodeURIComponent(key)}`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        await fetchDetail(selectedId);
        await fetchList();
      } else {
        alert('Falha ao sincronizar com Jira');
      }
    } catch (_) {
      alert('Erro de conexão ao sincronizar com Jira');
    } finally {
      setUpdatingStatus(false);
    }
  }

  async function handleQuickStatusUpdate(issueId, newStatus) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${issueId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        // Recarregar lista para mostrar a mudança
        fetchList();
      } else {
        const errorData = await response.json();
        alert('Erro ao atualizar status: ' + (errorData.error || 'Erro desconhecido'));
      }
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      alert('Erro de conexão ao atualizar status');
    }
  }

  async function handlePriorityChange(issueId, nextPriority) {
    if (!issueId || !nextPriority) return;
    const previousItems = items;
    setUpdatingPriorityId(issueId);
    setItems(prev => prev.map(item => (
      String(item.id) === String(issueId) ? { ...item, priority: nextPriority } : item
    )));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`issues/${issueId}/priority`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priority: nextPriority })
      });
      if (response.ok) {
        const payload = await response.json().catch(() => ({}));
        const updated = payload?.data;
        if (updated) {
          setItems(prev => prev.map(item => (
            String(item.id) === String(issueId) ? { ...item, ...updated } : item
          )));
          if (String(selectedId) === String(issueId) && detail?.issue) {
            setDetail(prev => prev ? ({ ...prev, issue: { ...prev.issue, ...updated } }) : prev);
          }
        }
      } else {
        setItems(previousItems);
        const errorData = await response.json().catch(() => ({}));
        alert('Erro ao atualizar prioridade: ' + (errorData.error || 'Erro desconhecido'));
      }
    } catch (err) {
      setItems(previousItems);
      console.error('Erro ao atualizar prioridade:', err);
      alert('Erro de conexão ao atualizar prioridade');
    } finally {
      setUpdatingPriorityId(null);
    }
  }

  async function handleOpenEvidence(evidence) {
    if (!evidence?.id) return;
    setLoadingPreviewEvidenceId(evidence.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`evidences/${evidence.id}/content`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const evidenceWithUrl = {
          ...evidence,
          url: URL.createObjectURL(blob),
          mime_type: blob.type || evidence.mime_type,
          isObjectUrl: true
        };
        closePreviewEvidence();
        setPreviewEvidence(evidenceWithUrl);
        return;
      }

      const viewResponse = await fetch(getApiUrl(`evidences/${evidence.id}/view`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const viewPayload = await viewResponse.json().catch(() => ({}));
      if (viewResponse.ok && viewPayload?.url) {
        closePreviewEvidence();
        setPreviewEvidence({ ...evidence, url: viewPayload.url, isObjectUrl: false });
        return;
      }

      const streamPayload = await response.json().catch(() => ({}));
      const message = viewPayload.detail || viewPayload.error || streamPayload.detail || streamPayload.error || 'Não foi possível carregar a evidência';
      alert(message);
    } catch (err) {
      console.error('Erro ao abrir evidência:', err);
      alert('Erro de conexão ao carregar evidência');
    } finally {
      setLoadingPreviewEvidenceId(null);
    }
  }

  async function handleDelete(issueId) {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl(`issues/${issueId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        if (selectedId === issueId) {
          closeModal();
        }
        // Fechar modal de confirmação, se aberto
        setShowDeleteIssueModal(false);
        setIssueToDelete(null);
        setItems(prev => prev.filter(i => String(i.id) !== String(issueId)));
        // Garantir consistência com backend
        fetchList();
      } else {
        const data = await resp.json().catch(() => ({}));
        showError(data.error || 'Erro ao excluir issue');
      }
    } catch (e) {
      console.error('Erro ao excluir issue:', e);
      showError('Erro de conexão ao excluir issue');
    }
  }

  function openDeleteIssueModal(issue) {
    setIssueToDelete(issue);
    setShowDeleteIssueModal(true);
  }

  function closeDeleteIssueModal() {
    setShowDeleteIssueModal(false);
    setIssueToDelete(null);
  }

  async function confirmDeleteIssue() {
    if (!issueToDelete?.id) {
      closeDeleteIssueModal();
      return;
    }
    await handleDelete(issueToDelete.id);
  }

  useEffect(() => {
    if (!canView) return;
    if (!isGerencial) return;
    if (hideResponsibleFilter) {
      setFilterDevs([]);
      setFilterQas([]);
      setQaOwnerId('');
      return;
    }
    fetchCompanyDevs();
    fetchCompanyQas();
  }, [canView, isGerencial, hideResponsibleFilter]);

  useEffect(() => {
    if (isGerencial) return;
    if (hideResponsibleFilter) return;
    const projects = Array.isArray(items) ? Array.from(new Set(items.map(it => String(it.project_id || '')))).filter(Boolean) : [];
    fetchFilterDevsForProjects(projects);
  }, [items, isGerencial, hideResponsibleFilter]);

  useEffect(() => {
    const pid = detail?.issue?.project_id;
    if (pid) {
      fetchProjectDevs(pid);
    } else {
      setProjectDevs([]);
    }
    const current = detail?.issue?.responsible_dev_user_id ? String(detail.issue.responsible_dev_user_id) : '';
    setSelectedResponsibleDev(current);
    setResponsibleDevInput(current ? (detail?.issue?.responsible_name || '') : '');
  }, [detail]);

  // Campo de responsável aceita digitar o nome (com sugestões da lista de
  // DEVs do projeto via <datalist>) ou clicar numa sugestão — nos dois casos
  // só resolve pra um user_id real quando o texto bate com um DEV
  // existente, senão "Definir" fica desabilitado (não dá pra atribuir
  // alguém que não está na lista, só digitar não basta).
  function handleResponsibleDevInputChange(value) {
    setResponsibleDevInput(value);
    const match = projectDevs.find(d => (d.name || '').trim().toLowerCase() === value.trim().toLowerCase());
    setSelectedResponsibleDev(match ? String(match.user_id) : '');
  }

  if (!canView) {
    return (
      <div className="issues-page">
        <div className="access-denied">
          <h2>🚫 Acesso Negado</h2>
          <p>Você não tem permissão para visualizar esta tela.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="issues-page">
      {showModal ? (
        renderDetail()
      ) : (
      <>
      <div className="page-header">
        <div>
          <h1>🐞 Bugs/Melhorias de Casos de Teste</h1>
          <p>Itens criados automaticamente quando um caso falha na execução.</p>
        </div>
        {!loading && !error && (
          <div className="issues-count-badge">
            {items.length} {items.length === 1 ? 'item' : 'itens'}
          </div>
        )}
      </div>

      {/* Filtros e busca */}
      <div className="filters">
        <div className="filter-field filter-field-search">
          <label>Buscar</label>
          <input
            type="text"
            placeholder="ID ou título do item"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label>Tipo</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {typeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {!hideResponsibleFilter && (
          <div className="filter-field">
            <label>Responsável</label>
            <select value={responsibleId} onChange={(e) => setResponsibleId(e.target.value)}>
              <option value="">Todos</option>
              {filterDevs.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name || u.email || `Usuário ${u.user_id}`}</option>
              ))}
            </select>
          </div>
        )}
        {isGerencial && (
          <div className="filter-field">
            <label>{"QA respons\u00E1vel"}</label>
            <select value={qaOwnerId} onChange={(e) => setQaOwnerId(e.target.value)}>
              <option value="">Todos</option>
              {filterQas.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || u.email || `Usu\u00E1rio ${u.user_id}`}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="filter-field">
          <label>De</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label>{"At\u00E9"}</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label>Projeto</label>
          <select
            className="project-filter"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Todos</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        <button className="btn btn-primary filter-search-btn" onClick={fetchList}>Buscar</button>
      </div>

      {/* Lista em cards — reflui em colunas conforme a largura da tela, nunca
          precisa de scroll lateral (diferente da tabela larga anterior). */}
      <div className="issues-list-wrapper">
        {loading ? (
          <div className="issues-state">Carregando...</div>
        ) : error ? (
          <div className="issues-state issues-state-error">{error}</div>
        ) : items.length === 0 ? (
          <div className="issues-state">
            <span className="issues-empty-icon">🗂️</span>
            Nenhum item encontrado com os filtros atuais
          </div>
        ) : (
          <div className="issues-grid">
            {items.map((it) => {
              const issueWorkflowStatus = it.issue_status || it.status;
              const currentCaseStatus = it.case_status || it.status;
              const normalizedPriority = it.priority === 'mdium' ? 'medium' : it.priority === 'critical' ? 'highest' : (it.priority || 'medium');

              return (
                <div key={it.id} className={`issue-card priority-edge-${normalizedPriority}`}>
                  <div className="issue-card-top">
                    <div className="issue-card-badges">
                      <span className={`type-badge type-${it.type}`}>
                        {TYPE_LABELS[it.type] || it.type}
                      </span>
                      <select
                        className={`priority-select priority-${normalizedPriority}`}
                        value={normalizedPriority}
                        onChange={(e) => handlePriorityChange(it.id, e.target.value)}
                        disabled={!canEditPriority || updatingPriorityId === it.id}
                        aria-label="Nível de severidade de bug/melhoria"
                      >
                        {PRIORITY_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    {renderCompactIdCell(it.id)}
                  </div>

                  <div className="issue-card-content">
                    <button type="button" className="issue-card-title" onClick={() => openDetail(it.id)}>
                      {it.test_case_title || it.title}
                    </button>
                    {it.test_case_id && (
                      <div className="issue-card-case-ref">Caso #{it.test_case_id}</div>
                    )}

                    <span className={`status-badge status-${currentCaseStatus}`}>
                      {CASE_STATUS_LABELS[currentCaseStatus] || STATUS_LABELS[currentCaseStatus] || currentCaseStatus}
                    </span>

                    {it.description && (
                      <div className="issue-card-description" title={it.description}>
                        {it.description}
                      </div>
                    )}
                  </div>

                  <div className="issue-card-info">
                    <div className="issue-card-meta">
                      <span title="Responsável DEV">👤 {it.responsible_name || 'Sem responsável'}</span>
                      <span title="QA responsável">🧪 {it.qa_opened_name || '-'}</span>
                    </div>
                    <div className="issue-card-dates">
                      <span>Aberto em {formatDate(it.opened_at || it.created_at)}</span>
                      <span>·</span>
                      <span>Atualizado {formatDate(it.updated_at)}</span>
                    </div>
                  </div>

                  <div className="issue-card-actions">
                    <button className="btn btn-sm" onClick={() => openDetail(it.id)}>
                      Detalhar
                    </button>
                    {canDeleteUI && !(['approved','fixed','in_progress'].includes(issueWorkflowStatus)) && (
                      <button className="btn btn-sm btn-danger" onClick={() => openDeleteIssueModal(it)}>
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}

      {/* Modal de confirmação de exclusão — único modal que resta: uma
          confirmação pontual, não uma visão de conteúdo. */}
      <DeleteIssueModal
        isOpen={showDeleteIssueModal}
        issue={issueToDelete}
        onClose={closeDeleteIssueModal}
        onConfirm={confirmDeleteIssue}
      />
    </div>
  );

  // Visão de detalhe do item — substitui a lista inline (sem overlay/modal),
  // com um botão de Voltar no topo. Antes era um modal; extraído aqui como
  // função porque precisa da mesma chave condicional (showModal) usada
  // acima pra decidir o que renderizar dentro de .issues-page.
  function renderDetail() {
    return (
          <div className="issue-detail-view">
            {detailLoading ? (
              <div className="loading">Carregando detalhes...</div>
            ) : detailError ? (
              <div className="error">{detailError}</div>
            ) : detail ? (
              <>
                <div className="issue-detail-header">
                  <div className="issue-detail-heading">
                    <h2>{detail.issue?.test_case_title || detail.issue?.title}</h2>
                    <div className="issue-detail-heading-badges">
                      <span className={`type-badge type-${detail.issue?.type}`}>
                        {TYPE_LABELS[detail.issue?.type] || detail.issue?.type}
                      </span>
                      <span className={`status-badge status-${detail.issue?.status}`}>
                        {STATUS_LABELS[detail.issue?.status] || detail.issue?.status}
                      </span>
                      {renderCompactIdCell(detail.issue?.id)}
                    </div>
                  </div>
                </div>
                <div className="issue-detail-body">
                  <div className="issue-detail-layout">
                    <div className="issue-detail-main">
                      <div className="detail-section">
                        <h3>Descrição</h3>
                        <p className="detail-description-text">{detail.issue?.description || 'Sem descrição.'}</p>
                      </div>

                      <div className="detail-section">
                        <h3>Evidências</h3>
                        {(!detail.evidences || detail.evidences.length === 0) ? (
                          <div className="muted">Nenhuma evidência anexada</div>
                        ) : (
                          <div className="issue-evidence-grid">
                            {detail.evidences.map((ev) => (
                              <button
                                type="button"
                                key={ev.id}
                                className="issue-evidence-card"
                                onClick={() => handleOpenEvidence(ev)}
                                disabled={loadingPreviewEvidenceId === ev.id}
                                title={ev.file_name}
                              >
                                <div className="issue-evidence-preview">
                                  {ev.url && ev.mime_type?.startsWith('image/') ? (
                                    <img src={ev.url} alt={ev.file_name} />
                                  ) : ev.url && ev.mime_type?.startsWith('video/') ? (
                                    <video src={ev.url} muted preload="metadata" />
                                  ) : (
                                    <span>
                                      {loadingPreviewEvidenceId === ev.id
                                        ? 'Carregando...'
                                        : ev.mime_type?.startsWith('video/')
                                          ? 'Vídeo'
                                          : 'Arquivo'}
                                    </span>
                                  )}
                                </div>
                                <div className="issue-evidence-name">{ev.file_name}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {(!isQaLike || (detail.issue?.opened_by_user_id === user?.id)) && (
                        <div className="detail-section">
                          <h3>Atualizar Status</h3>
                          <div className="actions-row">
                            {(canDevUpdate || (isQaLike && detail.issue?.opened_by_user_id === user?.id)) && (
                              <>
                                <button disabled={updatingStatus || detail.issue?.status === 'fixed'} onClick={() => handleUpdateStatus('in_progress')} className="btn btn-sm">Em correção</button>
                                <button disabled={updatingStatus} onClick={() => handleUpdateStatus('fixed')} className="btn btn-sm">Corrigido</button>
                              </>
                            )}
                            {(canQaUpdate || isQaLike) && (
                              <>
                                <button disabled={updatingStatus || detail.issue?.status !== 'fixed'} onClick={() => handleUpdateStatus('reopened')} className="btn btn-sm">Reabrir</button>
                                <button disabled={updatingStatus || detail.issue?.status === 'fixed'} onClick={() => handleUpdateStatus('rejected')} className="btn btn-sm">Reprovado</button>
                                <button disabled={updatingStatus} onClick={() => handleUpdateStatus('approved')} className="btn btn-sm">Aprovado</button>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="detail-section">
                        <h3>Comentários</h3>
                        <div className="comment-composer">
                          <textarea
                            rows={3}
                            placeholder="Escreva um comentário"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            maxLength={MAX_COMMENT_CHARS}
                          />
                          <div className="comment-composer-footer">
                            <span className={`comment-char-count ${overLimit ? 'over-limit' : ''}`}>{newComment.length}/{MAX_COMMENT_CHARS}</span>
                            <button className="btn btn-primary" onClick={handleAddComment} disabled={!newComment.trim() || overLimit}>Enviar</button>
                          </div>
                        </div>
                        {(!detail.comments || detail.comments.length === 0) ? (
                          <div className="muted">Sem comentários</div>
                        ) : (
                          <ul className="history-list">
                            {(detail.comments || []).map((c) => (
                              <li key={c.id}>
                                <div className="history-item">
                                  <div className="history-header">
                                    <span className="history-actor">{c.user_name || 'Usuário'}</span>
                                    <span className="history-date">{formatDate(c.created_at)}</span>
                                  </div>
                                  <div className="history-action">{c.comment}</div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <aside className="issue-detail-sidebar">
                      <div className="detail-section">
                        <h3>Propriedades</h3>
                        <dl className="properties-list">
                          <div className="property-row">
                            <dt>Severidade</dt>
                            <dd>
                              <span className={`priority-badge priority-${detail.issue?.priority === 'mdium' ? 'medium' : detail.issue?.priority === 'critical' ? 'highest' : (detail.issue?.priority || 'medium')}`}>
                                {PRIORITY_LABELS[detail.issue?.priority] || detail.issue?.priority || PRIORITY_LABELS.medium}
                              </span>
                            </dd>
                          </div>
                          <div className="property-row">
                            <dt>Responsável DEV</dt>
                            <dd>
                              <div className="property-value-text">{detail.issue?.responsible_name || 'Sem responsável'}</div>
                              {canQaUpdate && (
                                <div className="property-assign">
                                  <input
                                    type="text"
                                    list="responsible-dev-options"
                                    className="property-assign-input"
                                    placeholder="Selecione ou digite o nome do DEV"
                                    value={responsibleDevInput}
                                    onChange={(e) => handleResponsibleDevInputChange(e.target.value)}
                                  />
                                  <datalist id="responsible-dev-options">
                                    {projectDevs.map(d => (
                                      <option key={d.user_id} value={d.name} />
                                    ))}
                                  </datalist>
                                  <button
                                    className="btn btn-sm"
                                    onClick={handleAssignResponsible}
                                    disabled={assigningResponsible || !selectedResponsibleDev}
                                  >
                                    Definir
                                  </button>
                                </div>
                              )}
                              {isDev && !detail.issue?.responsible_dev_user_id && (
                                <button className="btn btn-sm property-claim-btn" onClick={handleClaim}>Reivindicar</button>
                              )}
                            </dd>
                          </div>
                          <div className="property-row">
                            <dt>QA responsável</dt>
                            <dd>{detail.issue?.qa_opened_name || '—'}</dd>
                          </div>
                          {detail.issue?.jira_issue_key && (
                            <div className="property-row">
                              <dt>Jira</dt>
                              <dd>
                                <div className="property-value-text">{detail.issue.jira_issue_key}</div>
                                <div className="property-assign">
                                  {detail.issue?.jira_issue_url ? (
                                    <a className="btn btn-sm" href={detail.issue.jira_issue_url} target="_blank" rel="noreferrer">Abrir</a>
                                  ) : null}
                                  <button className="btn btn-sm btn-primary" onClick={handleSyncWithJira} disabled={updatingStatus}>Sincronizar</button>
                                </div>
                              </dd>
                            </div>
                          )}
                          <div className="property-row">
                            <dt>Aberto em</dt>
                            <dd>{formatDate(detail.issue?.opened_at || detail.issue?.created_at)}</dd>
                          </div>
                          <div className="property-row">
                            <dt>Atualizado</dt>
                            <dd>{formatDate(detail.issue?.updated_at)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="detail-section">
                        <h3>Histórico de Movimentação</h3>
                        <ul className="history-list">
                          {(detail.history || []).map((h) => {
                            const isCorrection = h?.action === 'status_change' && /\b(fixed|approved)\b/i.test(h?.description || '');
                            return (
                              <li key={h.id}>
                                <div className={`history-item ${isCorrection ? 'correction' : ''}`}>
                                  <div className="history-header">
                                    <span className="history-actor">{h.user_name || 'Sistema'}</span>
                                    <span className="history-date">{formatDate(h.created_at)}</span>
                                  </div>
                                  <div className="history-action">
                                    {h.action} — {h.description}
                                    {isCorrection && (
                                      <span className="history-badge badge-correction" title="Bug/Melhoria marcado como corrigido/aprovado — reteste recomendado">Correção recebida</span>
                                    )}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </aside>
                  </div>
                </div>

                <div className="issue-detail-footer">
                  {canDeleteUI && detail?.issue?.id && !(['approved','fixed','in_progress'].includes(detail.issue?.status)) && (
                    <button className="btn btn-danger" onClick={() => openDeleteIssueModal(detail.issue)}>Excluir</button>
                  )}
                  <button className="btn" onClick={closeModal}>Voltar</button>
                </div>
                {previewEvidence && (
                  <div className="issue-evidence-preview-backdrop" onClick={closePreviewEvidence}>
                    <div className="issue-evidence-preview-modal" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="issue-evidence-preview-close"
                        onClick={closePreviewEvidence}
                      >
                        ×
                      </button>
                      {previewEvidence.mime_type?.startsWith('video/') ? (
                        <video src={previewEvidence.url} controls autoPlay />
                      ) : (
                        <img src={previewEvidence.url} alt={previewEvidence.file_name} />
                      )}
                      <div className="issue-evidence-preview-title">{previewEvidence.file_name}</div>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
    );
  }
}
